import { comics } from "@/mocks/comics";
import { formatViews } from "./format";

const ranked = [...comics].sort((a, b) => b.views - a.views).slice(0, 7);
const [featured, ...rest] = ranked;
const carousel = [...comics].sort((a, b) => b.likes - a.likes).slice(0, 8);

export default function HotSection() {
  return (
    <section id="hot" className="relative w-full bg-background-100 halftone py-12 md:py-16">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <span className="inline-flex items-center gap-1.5 text-accent-600 text-sm font-bold mb-1">
              <i className="ri-fire-fill" aria-hidden="true"></i>
              本周爆款
            </span>
            <h2 className="font-heading text-foreground-950 text-2xl md:text-4xl">热门推荐</h2>
          </div>
          <p className="hidden md:block text-sm text-foreground-600">
            大家正在追的沙雕神转折，点开就能乐
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Featured big card */}
          <a
            href="#follow"
            className="group relative rounded-2xl overflow-hidden border-2 border-background-200 hover:border-primary-400 hover:shadow-comic transition-all"
          >
            <div className="relative h-[300px] md:h-[420px] overflow-hidden">
              <img
                src={featured.cover}
                alt={`${featured.title}编辑精选封面`}
                loading="lazy"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/20 to-transparent"></div>
              <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                本周第1名
              </span>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-md bg-secondary-400 text-foreground-950 text-[11px] font-bold">
                    {featured.category}
                  </span>
                  <span className="px-2 py-0.5 rounded-md bg-foreground-950/50 text-background-50 text-[11px]">
                    {featured.status} · {featured.chapters}话
                  </span>
                </div>
                <h3 className="font-heading text-background-50 text-xl md:text-3xl">{featured.title}</h3>
                <p className="text-background-100/85 text-xs md:text-sm mt-1.5 line-clamp-2">
                  {featured.description}
                </p>
                <span className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-bold">
                  立即开看 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </div>
            </div>
          </a>

          {/* Ranked list */}
          <div className="flex flex-col gap-3">
            {rest.map((c, i) => (
              <a
                key={c.id}
                href="#follow"
                className="group flex items-center gap-3 p-2.5 rounded-xl bg-background-50 border-2 border-background-200 hover:border-primary-300 transition"
              >
                <span className="w-8 h-8 shrink-0 rounded-lg bg-primary-100 text-primary-700 font-heading text-lg flex items-center justify-center">
                  {i + 2}
                </span>
                <div className="w-14 h-[70px] shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={c.cover}
                    alt={`${c.title}封面缩略图`}
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-foreground-950 text-sm md:text-base truncate">
                    {c.title}
                  </h3>
                  <p className="text-foreground-600 text-xs truncate mt-0.5">{c.description}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {formatViews(c.views)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-book-open-line" aria-hidden="true"></i>
                      {c.chapters}话
                    </span>
                    <span className="ml-auto hidden sm:block">{c.update}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Carousel strip */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-heart-3-fill text-accent-500" aria-hidden="true"></i>
            <h3 className="font-heading text-foreground-950 text-lg md:text-xl">大家都在追</h3>
          </div>
          <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth snap-x">
            {carousel.map((c) => (
              <a
                key={c.id}
                href="#follow"
                className="group shrink-0 snap-start w-28 md:w-36 rounded-xl overflow-hidden bg-background-50 border-2 border-background-200 hover:border-primary-400 hover:shadow-comic-sm transition"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-background-100">
                  <img
                    src={c.cover}
                    alt={`${c.title}追更封面`}
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {c.badge && (
                    <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px] font-bold">
                      {c.badge}
                    </span>
                  )}
                </div>
                <div className="p-2">
                  <h4 className="font-heading text-foreground-950 text-xs md:text-sm truncate">
                    {c.title}
                  </h4>
                  <p className="text-foreground-500 text-[11px] mt-1 truncate">
                    {formatViews(c.views)} 热度
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}